import type { ReactNode } from 'react';
import { usePhoneActions } from '../../hooks/usePhoneActions';
import { useTelephonyClient } from '../../hooks/useTelephonyClient';
import type { Brand } from '../../lib/telephony';
import { isDialablePhone } from '../../lib/telephony';
import { PhoneIcon } from './icons';

interface Props {
  number: string | null | undefined;
  brand?: Brand;
  customerId?: string;
  customerName?: string;
  sourceContext?: string;
  variant?: 'primary' | 'icon' | 'inline';
  title?: string;
  children?: ReactNode;
}

export function CallButton({
  number,
  brand,
  customerId,
  customerName,
  sourceContext,
  variant = 'primary',
  title,
  children,
}: Props) {
  // Zet het nummer klaar in de docked softphone (voorgevuld) i.p.v. direct te
  // bellen — de gebruiker klikt zelf op Bellen. Spiegelt <PhoneNumber>; we bellen
  // nooit stil bij één klik.
  const { openDialPad } = usePhoneActions();
  // Eén-lijns-softphone: tijdens een levend gesprek toont de softphone het
  // actieve gesprek (dialpad is dan verborgen), dus klaarzetten heeft geen zin.
  const { activeCall } = useTelephonyClient();
  const inCall = Boolean(activeCall);
  const valid = isDialablePhone(number) && !inCall;

  const className =
    variant === 'icon'
      ? 'inline-flex items-center justify-center p-2 rounded-full text-emerald-700 hover:bg-emerald-50 disabled:opacity-40 disabled:cursor-not-allowed'
      : variant === 'inline'
      ? 'inline-flex items-center gap-1 text-sm text-emerald-700 hover:underline disabled:text-gray-400 disabled:no-underline'
      : 'inline-flex items-center gap-2 px-3 py-2 rounded-md bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed';

  const labelForAria = customerName ? `Bel ${customerName}` : number ? `Bel ${number}` : 'Bel';

  return (
    <button
      type="button"
      className={className}
      disabled={!valid}
      aria-label={labelForAria}
      title={
        inCall
          ? 'Je bent nog in gesprek. Beëindig het huidige gesprek eerst.'
          : title ?? (valid ? `${number} in de dialpad plaatsen` : 'Geen geldig telefoonnummer')
      }
      onClick={() => {
        if (!valid || !number) return;
        openDialPad({ number, brand, customerId, customerName, sourceContext });
      }}
    >
      {children ?? (
        <>
          <PhoneIcon className="w-4 h-4 shrink-0" />
          {variant !== 'icon' && <span>Bel</span>}
        </>
      )}
    </button>
  );
}
